import React, { useState } from "react";
import IconPassword from "../components/Icons/IconPassword";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/UserAPIUtils";
import { AxiosError } from "axios";

interface ForgottenPasswordWindowProps {}

const ResetPasswordPage: React.FC<ForgottenPasswordWindowProps> = ({}) => {
  const [loading, setLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (e.target.value !== confirmPassword) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError("Password must be at least 8 characters long");
    } else {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
    if (e.target.value !== password) {
      setError("Passwords do not match");
    } else if (e.target.value.length < 8) {
      setError("Password must be at least 8 characters long");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPasswordReset) {
      navigate("/");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const passwordRegex =
      /^(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{}|;:,.<>?])[A-Za-z\d!@#$%^&*()_+\[\]{}|;:,.<>?]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError(
        "Password must be at least 8 characters, include 1 number, 1 special character, and 1 uppercase letter."
      );
      return;
    }
    if (token === null) {
      setError("Invalid token");
      return;
    }
    try {
      setLoading(true);
      const response = await resetPassword(password, token);
      if (response) {
        setIsPasswordReset(true);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "An unexpected error occurred.";
        setError(errorMessage);
      } else {
        setError("An unexpected error occurred.");
      }
      console.error("Failed to reset password", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div
        className="flex flex-col justify-center bg-white shadow rounded-lg p-8 w-full h-full
                    lg:w-1/2 lg:h-auto"
      >
        <div className="flex flex-col justify-center items-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary mb-6">
            <IconPassword width="35px" height="35px" />
          </div>
          {!isPasswordReset ? (
            <>
              <h2 className="text-2xl font-bold text-primary mb-4">
                Set a new Password
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                Must be at least 8 characters.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-primary mb-4">
                Password Reset Successful
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                You can now return to the login page.
              </p>
            </>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          {!isPasswordReset && (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Password:</label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">
                  Confirm Password:
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            </>
          )}
          <button
            type="submit"
            disabled={loading}
            onClick={handleSubmit}
            className="w-full bg-primary text-white p-2 rounded hover:bg-primary-dark"
          >
            {isPasswordReset ? "Return to Login" : "Reset Password"}
          </button>
          {error && (
            <p className="text-red-500 text-center mb-4 mt-2 px-2">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
